import { PhoneNumberCountryCallingCode } from '@seek/profile-edit-ui';
import { useCallback, useEffect, useRef } from 'react';
import { useController, type Control } from 'react-hook-form';

import { Page, PageSection, useTrackingClient } from 'src/context/tracking';

import type { DefaultValues } from './types';

interface PhoneFieldProps {
  control: Control<DefaultValues>;
  onFocus: () => void;
}

const PhoneField: React.FC<PhoneFieldProps> = ({ control, onFocus }) => {
  const trackingClient = useTrackingClient();

  const { field: phoneNumber, fieldState: phoneNumberState } = useController({
    name: 'phoneNumber',
    control,
  });
  const { field: countryCodeId, fieldState: countryCodeIdState } =
    useController({
      name: 'countryCodeId',
      control,
    });

  const listenToBlur = useRef<HTMLInputElement>(null);

  const handleBlur = useCallback(() => {
    phoneNumber.onBlur();
    countryCodeId.onBlur();
  }, [phoneNumber, countryCodeId]);

  useEffect(() => {
    const node = listenToBlur.current;
    if (node) {
      node.addEventListener('blur', handleBlur);
      node.addEventListener('focus', onFocus);
    }
    return () => {
      if (node) {
        node.removeEventListener('blur', handleBlur);
        node.removeEventListener('focus', onFocus);
      }
    };
  }, [handleBlur, onFocus]);

  const hasError = phoneNumberState.invalid || countryCodeIdState.invalid;
  return (
    <PhoneNumberCountryCallingCode
      ref={listenToBlur}
      phoneNumber={phoneNumber.value}
      countryCallingCode={{ id: countryCodeId.value }}
      onPhoneNumberChange={(p) => phoneNumber.onChange(p || '')}
      onCountryCallingCodeChange={({ id }) => {
        trackingClient.trackLink({
          link: 'apply_field_pressed',
          formField: 'country-code',
          currentPage: Page.DOCUMENT_PAGE,
          currentPageSection: PageSection.PERSONAL_DETAILS,
        });
        return countryCodeId.onChange(id);
      }}
      message={
        phoneNumberState.error?.message || countryCodeIdState.error?.message
      }
      tone={hasError ? 'critical' : undefined}
    />
  );
};

export default PhoneField;
